import { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useEffect } from "react";
import { useQuota } from "../contexts/QuotaContext";
import {
  FolderOpen,
  Upload,
  Plus,
  ArrowRight,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  FileSpreadsheet,
  Calendar,
  Trash2,
  AlertTriangle,
  Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  failed: "bg-red-500/10 text-red-400 border-red-500/20",
  running: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  queued: "bg-blue-500/10 text-blue-400 border-blue-500/20",
};

const statusIcons = {
  completed: CheckCircle2,
  failed: XCircle,
  running: Loader2,
  queued: Clock,
};

const Batches = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [batchName, setBatchName] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const { unlimited, remaining, used, limit: quotaLimit, canUse, fetchQuota } = useQuota();
  const batchExhausted = !unlimited && !canUse("batch_csv");

  const fetchBatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/batches`);
      setBatches(response.data.batches);
    } catch (error) {
      console.error("Failed to fetch batches:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const deleteBatch = async (batchId, batchName) => {
    if (!window.confirm(`Delete batch "${batchName}" and all its searches? This cannot be undone.`)) {
      return;
    }
    
    setDeleting(batchId);
    try {
      const response = await axios.delete(`${API}/batch/${batchId}`);
      setBatches(batches.filter(b => b.id !== batchId));
      toast.success(`Batch deleted (${response.data.searches_deleted} searches removed)`);
    } catch (error) {
      toast.error("Failed to delete batch");
      console.error("Delete error:", error);
    } finally {
      setDeleting(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith(".csv")) {
        toast.error("Please select a CSV file");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }
    if (!batchName.trim()) {
      toast.error("Please enter a batch name");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", batchName.trim());
      if (notes.trim()) {
        formData.append("notes", notes.trim());
      }

      const response = await axios.post(`${API}/batch?name=${encodeURIComponent(batchName.trim())}&notes=${encodeURIComponent(notes.trim())}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const skipped = response.data.skipped_rows || 0;
      if (skipped > 0) {
        toast.success(`Batch created with ${response.data.total_rows} rows (${skipped} rows skipped - missing title)`);
      } else {
        toast.success(`Batch created with ${response.data.total_rows} rows`);
      }
      setShowUpload(false);
      setBatchName("");
      setNotes("");
      setSelectedFile(null);
      fetchQuota();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-syncchain-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="batches-page">
      {/* Quota Banner */}
      {!unlimited && (
        <div
          className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
            batchExhausted
              ? "bg-red-500/10 border-red-500/30 text-red-300"
              : "bg-syncchain-accent/10 border-syncchain-accent/30 text-syncchain-text-secondary"
          }`}
          data-testid="batch-quota-banner"
        >
          <div className="flex items-center gap-2 text-sm">
            {batchExhausted ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <FolderOpen className="w-4 h-4" />}
            <span>
              {batchExhausted
                ? "Batch CSV uploads are not available on your current plan."
                : `${used("batch_csv")} / ${quotaLimit("batch_csv")} batch uploads used`}
            </span>
          </div>
          {batchExhausted ? (
            <Link to="/pricing" className="flex items-center gap-1 text-xs font-semibold text-syncchain-accent hover:text-syncchain-accent-hover transition-colors">
              <Zap className="w-3 h-3" /> Upgrade
            </Link>
          ) : (
            <span className="text-xs font-mono">{remaining("batch_csv")} remaining</span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-syncchain-text">
            Batch Processing
          </h1>
          <p className="text-syncchain-text-secondary mt-1">
            Upload CSV files for bulk track resolution
          </p>
        </div>
        <Button
          onClick={() => setShowUpload(true)}
          disabled={batchExhausted}
          className="bg-syncchain-accent hover:bg-syncchain-accent-hover text-white rounded-full px-6 glow-hover disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="new-batch-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Batch
        </Button>
      </div>

      {/* Upload Card */}
      {showUpload && (
        <Card className="bg-syncchain-paper border-white/10 animate-slide-up">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-syncchain-text flex items-center gap-2">
              <Upload className="w-5 h-5 text-syncchain-accent" />
              Upload CSV
            </CardTitle>
            <CardDescription className="text-syncchain-text-secondary">
              <span className="block mb-2">Required columns: <span className="text-syncchain-accent">Title</span> and <span className="text-syncchain-accent">Artist</span> (or Performer)</span>
              <span className="text-xs text-syncchain-text-muted">Optional: ISRC, Spotify URL</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* CSV Format Help */}
            <div className="bg-syncchain-surface/50 rounded-lg p-3 border border-white/5">
              <p className="text-xs font-mono text-syncchain-text-muted mb-2">Accepted CSV headers:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-syncchain-accent">Title:</span>
                  <span className="text-syncchain-text-secondary ml-1">title, track_title, song</span>
                </div>
                <div>
                  <span className="text-syncchain-accent">Artist:</span>
                  <span className="text-syncchain-text-secondary ml-1">artist, performer</span>
                </div>
                <div>
                  <span className="text-syncchain-text-muted">ISRC:</span>
                  <span className="text-syncchain-text-secondary ml-1">isrc (optional)</span>
                </div>
                <div>
                  <span className="text-syncchain-text-muted">Spotify:</span>
                  <span className="text-syncchain-text-secondary ml-1">spotify_url (optional)</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                Batch Name *
              </Label>
              <Input
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="e.g., Q1 2024 Sync Requests"
                className="bg-syncchain-surface border-white/10 text-syncchain-text"
                data-testid="batch-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                Notes
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes about this batch..."
                className="bg-syncchain-surface border-white/10 text-syncchain-text resize-none"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                CSV File *
              </Label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add('border-syncchain-accent', 'bg-syncchain-accent/5');
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('border-syncchain-accent', 'bg-syncchain-accent/5');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('border-syncchain-accent', 'bg-syncchain-accent/5');
                  const files = e.dataTransfer.files;
                  if (files && files[0]) {
                    if (!files[0].name.endsWith('.csv')) {
                      toast.error('Please drop a CSV file');
                      return;
                    }
                    setSelectedFile(files[0]);
                  }
                }}
                className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-syncchain-accent/50 transition-colors"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                <FileSpreadsheet className="w-12 h-12 text-syncchain-text-muted mx-auto mb-3" />
                {selectedFile ? (
                  <p className="text-syncchain-text">{selectedFile.name}</p>
                ) : (
                  <>
                    <p className="text-syncchain-text">Click to select CSV file</p>
                    <p className="text-xs text-syncchain-text-muted mt-1">
                      or drag and drop
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowUpload(false);
                  setBatchName("");
                  setNotes("");
                  setSelectedFile(null);
                }}
                variant="outline"
                className="flex-1 border-white/10 text-syncchain-text-secondary hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || !selectedFile || !batchName.trim()}
                className="flex-1 bg-syncchain-accent hover:bg-syncchain-accent-hover text-white"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload & Process
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batches List */}
      {batches.length > 0 ? (
        <div className="space-y-4">
          {batches.map((batch) => {
            const StatusIcon = statusIcons[batch.status] || Clock;
            const progress = batch.total_rows > 0
              ? Math.round(((batch.completed_rows + batch.failed_rows) / batch.total_rows) * 100)
              : 0;

            return (
              <Card 
                key={batch.id}
                className="bg-syncchain-paper border-white/10 hover:border-syncchain-accent/30 transition-all group cursor-pointer"
                data-testid={`batch-${batch.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <Link to={`/batch/${batch.id}`} className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading font-semibold text-lg text-syncchain-text truncate">
                          {batch.name}
                        </h3>
                        <Badge
                          variant="outline"
                          className={`${statusColors[batch.status]} text-xs font-mono uppercase shrink-0`}
                        >
                          <StatusIcon className={`w-3 h-3 mr-1 ${batch.status === 'running' ? 'animate-spin' : ''}`} />
                          {batch.status}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-syncchain-text-secondary">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(batch.created_at)}
                        </span>
                        <span>{batch.total_rows} tracks</span>
                      </div>

                      {batch.notes && (
                        <p className="text-sm text-syncchain-text-muted mt-2 truncate">
                          {batch.notes}
                        </p>
                      )}

                      {batch.status === "running" && (
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-syncchain-text-muted mb-1">
                            <span>Progress</span>
                            <span>{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-1.5 bg-syncchain-surface" />
                        </div>
                      )}

                      {batch.status === "completed" && (
                        <div className="flex gap-4 mt-3 text-xs">
                          <span className="text-syncchain-success">
                            ✓ {batch.completed_rows} completed
                          </span>
                          {batch.failed_rows > 0 && (
                            <span className="text-syncchain-error">
                              ✗ {batch.failed_rows} failed
                            </span>
                          )}
                        </div>
                      )}
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteBatch(batch.id, batch.name)}
                        disabled={deleting === batch.id}
                        className="text-syncchain-text-muted hover:text-red-400 hover:bg-red-500/10 z-10"
                        data-testid={`delete-batch-${batch.id}`}
                      >
                        {deleting === batch.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                      <Link to={`/batch/${batch.id}`}>
                        <ArrowRight className="w-5 h-5 text-syncchain-text-muted group-hover:text-syncchain-accent transition-colors" />
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-syncchain-paper border-white/10">
          <CardContent className="py-16 text-center">
            <FolderOpen className="w-16 h-16 text-syncchain-text-muted mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-syncchain-text mb-2">
              No batches yet
            </h3>
            <p className="text-syncchain-text-secondary mb-6">
              Upload a CSV file to process multiple tracks at once
            </p>
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-syncchain-accent hover:bg-syncchain-accent-hover text-white rounded-full px-6"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload First Batch
            </Button>
          </CardContent>
        </Card>
      )}

      {/* CSV Format Help */}
      <Card className="bg-syncchain-surface/50 border-white/5">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold text-syncchain-text mb-3">
            CSV Format
          </h3>
          <p className="text-sm text-syncchain-text-secondary mb-4">
            Your CSV file should include the following headers:
          </p>
          <div className="bg-syncchain-bg rounded-lg p-4 font-mono text-sm text-syncchain-text-secondary overflow-x-auto">
            <code>title,artist,isrc,spotify_url</code>
            <br />
            <code className="text-syncchain-text-muted">Bohemian Rhapsody,Queen,GBUM71029604,https://open.spotify.com/track/...</code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Batches;
